// OP-3619 — Ler peça atrás de peça, sem fechar a câmera.
//
// Para as telas de marcar peça (contagem, coleta, conferência), onde o
// operador passa por dezenas de etiquetas seguidas. Cada leitura vai para
// `aoLer`, que diz o que aconteceu; as leituras entram numa fila, na ordem em
// que chegaram, para uma não atropelar a gravação da anterior. Concluir espera
// a fila esvaziar antes de fechar: quem recarrega a tela em `aoFechar` não
// pode ler o estado de antes da última gravação.
import { FC, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { CameraScanner, ScanFeedbackMessage } from '../../../components/CameraScanner'
import { Button } from '../styles'
import { IconeCamera } from './CampoComLeitor'
import { RetornoLeitura, normalizarCodigo } from './leitura'
import { useCameraDisponivel } from './useCameraDisponivel'

const TOM: Record<RetornoLeitura['tom'], ScanFeedbackMessage['tone']> = { ok: 'ok', aviso: 'warn', erro: 'error' }

export interface LeitorContinuoProps {
  titulo: string
  rotulo?: string
  aoLer: (codigo: string) => RetornoLeitura | Promise<RetornoLeitura>
  aoAbrir?: () => void
  /** Hora de recarregar a tela: roda depois da última leitura gravada. */
  aoFechar?: () => void
  disabled?: boolean
}

export const LeitorContinuo: FC<LeitorContinuoProps> = ({ titulo, rotulo = 'Ler peças com a câmera', aoLer, aoAbrir, aoFechar, disabled }) => {
  const comCamera = useCameraDisponivel()
  const [aberto, setAberto] = useState(false)
  const [concluindo, setConcluindo] = useState(false)
  const [retorno, setRetorno] = useState<RetornoLeitura | null>(null)
  const [lidas, setLidas] = useState(0)
  const aoLerRef = useRef(aoLer)
  aoLerRef.current = aoLer
  const fila = useRef<Promise<void>>(Promise.resolve())
  const pendentes = useRef(0)
  const fechando = useRef(false)
  const falhasAoConcluir = useRef(0)

  if (!comCamera) return null

  const abrir = () => {
    setRetorno(null)
    setLidas(0)
    fechando.current = false
    falhasAoConcluir.current = 0
    if (aoAbrir) aoAbrir()
    setAberto(true)
  }

  const ler = (codigo: string) => {
    if (fechando.current) return
    const c = normalizarCodigo(codigo)
    pendentes.current += 1
    fila.current = fila.current
      .then(async () => {
        const r = await aoLerRef.current(c)
        setRetorno(r)
        if (r.tom === 'ok') setLidas((n) => n + 1)
        if (r.tom === 'erro' && fechando.current) falhasAoConcluir.current += 1
      })
      .catch(() => {
        setRetorno({ texto: `Não foi possível registrar ${c}. Leia de novo.`, tom: 'erro' })
        if (fechando.current) falhasAoConcluir.current += 1
      })
      .finally(() => { pendentes.current -= 1 })
  }

  const fechar = async () => {
    if (fechando.current) return
    fechando.current = true
    if (pendentes.current > 0) {
      setConcluindo(true)
      await fila.current
      setConcluindo(false)
    }
    setAberto(false)
    if (falhasAoConcluir.current > 0) {
      const n = falhasAoConcluir.current
      toast.error(`${n} ${n === 1 ? 'leitura não foi registrada' : 'leituras não foram registradas'} ao concluir. Confira a lista.`)
    }
    if (aoFechar) aoFechar()
  }

  const feedback: ScanFeedbackMessage | null = concluindo
    ? { text: 'Concluindo as leituras pendentes…', tone: 'warn' }
    : retorno ? { text: retorno.texto, tone: TOM[retorno.tom] } : null

  return (
    <>
      <Button type="button" $variant="secondary" disabled={disabled} onClick={abrir}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <IconeCamera />{rotulo}
      </Button>
      {aberto && (
        <CameraScanner
          continuous
          title={titulo}
          subtitle={lidas > 0 ? `${lidas} ${lidas === 1 ? 'peça registrada' : 'peças registradas'} nesta leitura` : undefined}
          hint="Aponte para a etiqueta de cada peça. A câmera continua aberta até você concluir."
          manualLabel="Concluir leitura"
          feedback={feedback}
          onScan={ler}
          onClose={fechar}
          onManual={fechar}
        />
      )}
    </>
  )
}

export default LeitorContinuo
